// src/hooks/usePrivateTimeCapsuleSend.js
import { useCallback, useMemo, useRef, useState } from "react";

// Firebase
import { db, auth, storage, functions as firebaseFunctions } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  updateDoc,
  getDoc,
  doc,
} from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import {
  buildVideoPosterFileFromPreview,
  buildUploadableMediaFile,
  buildVideoSessionPreviewDataUrl,
  isForwardedSourceMediaItem,
  isGooglePlaceImportUrlItem,
  shouldUploadWizardMediaItem,
} from "../utils/timeCapsuleMedia";
import { uploadWithRetries } from "../utils/resumableUpload";

// Geohash
import ngeohash from "ngeohash";

const TRIGGER_KEYS = {
  PRIVATE_TC_SENT: "private_tc_drop",
  PRIVATE_TC_RECIPIENT: "private_tc_recipient",
  PRIVATE_TC_NEW_RECIPIENT: "private_tc_new_recipient",
};
const VIDEO_PREVIEW_BEST_EFFORT_MS = 2500;

function normalizeUid(u) {
  if (!u) return "";
  return String(u).trim();
}

function uniq(arr) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

function asInt(v, fallback) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
}

function parseOptionalDate(value) {
  if (!value) return null;
  const nextDate = value instanceof Date ? value : new Date(value);
  return Number.isNaN(nextDate.getTime()) ? null : nextDate;
}

function normalizeSeedType(value) {
  const next = String(value || "").trim().toLowerCase();
  return ["see", "eat", "drink", "do", "shop", "relax"].includes(next) ? next : "";
}

function pickTriggerValue(data, key) {
  if (!data || typeof data !== "object") return null;

  const candidates = [
    data?.pointsTriggers?.[key],
    data?.points?.triggers?.[key],
    data?.points?.[key],
    data?.triggers?.[key],
    data?.[key],
  ];

  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n)) return Math.trunc(n);
  }
  return null;
}

/**
 * Reads the trigger config from the NEW schema first:
 *   config/points/triggers/{triggerKey} -> { enabled, points }
 * Falls back to older config docs if needed.
 */
async function getTriggerConfig(triggerKey, fallbackPoints = 0) {
  // NEW schema: config (collection) / points (doc) / triggers (subcollection) / {triggerKey} (doc)
  try {
    const tRef = doc(db, "config", "points", "triggers", triggerKey);
    const snap = await getDoc(tRef);
    if (snap.exists()) {
      const data = snap.data() || {};
      const enabledRaw = data.enabled;
      const enabled = enabledRaw !== false; // default to true if missing
      const points = asInt(data.points, fallbackPoints);
      return { enabled, points };
    }
  } catch {
    // ignore and try legacy
  }

  // Legacy fallbacks (no enabled flag in these older layouts, so assume enabled)
  const docsToTry = [
    doc(db, "config", "app"),
    doc(db, "config", "points"),
    doc(db, "config", "pointsTriggers"),
  ];

  for (const dref of docsToTry) {
    try {
      const snap = await getDoc(dref);
      if (!snap.exists()) continue;
      const val = pickTriggerValue(snap.data(), triggerKey);
      if (val !== null) return { enabled: true, points: val };
    } catch {
      // ignore and try next
    }
  }

  return { enabled: true, points: asInt(fallbackPoints, 0) };
}

function toMediaEntry(mediaFile, downloadURL) {
  if (!mediaFile) return null;

  const url = downloadURL || mediaFile.url;
  if (!url) return null;

  const typeStr = String(mediaFile.type || "").toLowerCase();
  const kind = typeStr.startsWith("video") ? "video" : "image";
  const source = mediaFile?.source ? String(mediaFile.source) : "";
  const storagePath = mediaFile?.storagePath ? String(mediaFile.storagePath) : "";
  const sessionPreviewUrl = String(mediaFile?.sessionPreviewUrl || "").trim();
  const posterUrl = String(mediaFile?.posterUrl || "").trim();
  const thumbnailUrl = String(mediaFile?.thumbnailUrl || posterUrl).trim();
  const posterStoragePath = String(mediaFile?.posterStoragePath || "").trim();
  const hasSessionPreview =
    kind === "video" &&
    sessionPreviewUrl.startsWith("data:image/") &&
    !thumbnailUrl;
  const draftOwnedMedia = mediaFile?.draftOwnedMedia === true;

  return {
    type: kind,
    url,
    ...(draftOwnedMedia ? { draftOwnedMedia: true } : {}),
    ...(source ? { source } : {}),
    ...(storagePath ? { storagePath } : {}),
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(posterUrl ? { posterUrl } : {}),
    ...(posterStoragePath ? { posterStoragePath } : {}),
    ...(hasSessionPreview ? { sessionPreviewUrl } : {}),
  };
}

async function uploadVideoPosterThumbnail({ sessionPreviewUrl = "", sourceName = "", index = 0 } = {}) {
  const posterFile = buildVideoPosterFileFromPreview(sessionPreviewUrl, sourceName);
  if (!posterFile) return {};

  const posterPath = `timecapsules/thumbnails/${Date.now()}-${index}-${posterFile.name}`;
  const posterRef = ref(storage, posterPath);
  await uploadWithRetries({
    storageRef: posterRef,
    file: posterFile,
    metadata: { contentType: posterFile.type || "image/webp" },
    maxRetries: 2,
    stallMs: 15000,
  });
  const posterUrl = await getDownloadURL(posterRef);
  return {
    thumbnailUrl: posterUrl,
    posterUrl,
    posterStoragePath: posterPath,
  };
}

function buildCapsulePreviewPayload({
  docId = "",
  senderUid = "",
  senderProfileImage = "",
  audience = "share",
  address = "",
  placeId = "",
  lat = 0,
  lng = 0,
  mediaEntries = [],
  subject = "",
  message = "",
  seedType = "",
  revealDate = null,
  finalRecipientUserIds = [],
  finalStatus = "sent",
}) {
  return {
    id: String(docId || "").trim(),
    senderId: String(senderUid || "").trim(),
    senderProfileImage: String(senderProfileImage || "").trim(),
    audience: String(audience || "share"),
    isSelfCapsule: audience === "self",
    location: {
      address: String(address || "").trim(),
      placeId: String(placeId || "").trim(),
      lat: Number(lat),
      lng: Number(lng),
    },
    media: Array.isArray(mediaEntries) ? mediaEntries : [],
    messageTitle: String(subject || "").trim(),
    messageBody: String(message || "").trim(),
    seedType: normalizeSeedType(seedType),
    revealDate: parseOptionalDate(revealDate),
    recipientPhoneNumbers: [],
    recipientUserIds: Array.isArray(finalRecipientUserIds) ? finalRecipientUserIds : [],
    deliveryMode: "share_claim",
    claimedBy: Array.isArray(finalRecipientUserIds) ? finalRecipientUserIds : [],
    capsuleType: "private",
    status: String(finalStatus || "sent").trim().toLowerCase(),
    lifecycleStatus: String(finalStatus || "sent").trim().toLowerCase(),
    isDraft: false,
  };
}

async function buildVideoPreviewBestEffort(file) {
  try {
    return await Promise.race([
      buildVideoSessionPreviewDataUrl(file),
      new Promise((resolve) => {
        setTimeout(() => resolve(""), VIDEO_PREVIEW_BEST_EFFORT_MS);
      }),
    ]);
  } catch {
    return "";
  }
}

export default function usePrivateTimeCapsuleSend({ wizardData }) {
  const importGooglePlacePhotoToStorage = useMemo(
    () => httpsCallable(firebaseFunctions, "importGooglePlacePhotoToStorage"),
    []
  );
  const copyForwardedTimeCapsuleMediaToStorage = useMemo(
    () => httpsCallable(firebaseFunctions, "copyForwardedTimeCapsuleMediaToStorage"),
    []
  );

  const {
    lat,
    lng,
    address,
    placeId = "",
    subject,
    message,
    seedType = "",
    mediaFile,
    mediaFiles = [],
    audience = "share",
    recipients = [],
    revealDate,
    endDate,
    callToActionText = "",
    callToActionUrl = "",
    hashtags = [],
    isBusinessCapsule = false,
    draftId = "",
    draftStep = "compose",
    sourceCapsuleId = "",
    forwardSourceId = "",
    forwardedFromId = "",
    forwardedFromType = "",
    sourceShareToken = "",
  } = wizardData || {};

  const normalizedMediaFiles = useMemo(() => {
    if (Array.isArray(mediaFiles) && mediaFiles.length) return mediaFiles;
    return mediaFile ? [mediaFile] : [];
  }, [mediaFiles, mediaFile]);

  const hasUpload = normalizedMediaFiles.some((m) => shouldUploadWizardMediaItem(m));

  const [isSending, setIsSending] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [savedDocId, setSavedDocId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorSnack, setShowErrorSnack] = useState(false);

  const [earnedPoints, setEarnedPoints] = useState(0);
  const earnedPointsRef = useRef(0);

  const uploadTaskRef = useRef(null);

  const clearError = useCallback(() => {
    setError(false);
    setErrorMessage("");
    setShowErrorSnack(false);
  }, []);

  /**
   * Computes points preview, respecting enabled flags.
   * - private_tc_drop: once per capsule
   * - private_tc_recipient: per recipient (all recipients)
   * - private_tc_new_recipient: per *non-member* recipient (SMS invite list only)
   */
  const computeEarnedPointsPreview = useCallback(
    async ({ recipientCount = 0, newRecipientCount = 0 } = {}) => {
      const perDropFallback = 5;
      const perRecipientFallback = 5;
      const perNewRecipientFallback = 0; // safe default so this doesn't change behavior if missing

      const dropCfg = await getTriggerConfig(TRIGGER_KEYS.PRIVATE_TC_SENT, perDropFallback);
      const perRecCfg = await getTriggerConfig(
        TRIGGER_KEYS.PRIVATE_TC_RECIPIENT,
        perRecipientFallback
      );
      const newRecCfg = await getTriggerConfig(
        TRIGGER_KEYS.PRIVATE_TC_NEW_RECIPIENT,
        perNewRecipientFallback
      );

      const dropPts = dropCfg.enabled ? Math.max(0, asInt(dropCfg.points, 0)) : 0;
      const perRecPts = perRecCfg.enabled ? Math.max(0, asInt(perRecCfg.points, 0)) : 0;
      const newRecPts = newRecCfg.enabled ? Math.max(0, asInt(newRecCfg.points, 0)) : 0;

      const total =
        dropPts +
        perRecPts * Math.max(0, recipientCount) +
        newRecPts * Math.max(0, asInt(newRecipientCount, 0));

      return Math.max(0, total);
    },
    []
  );

  const send = useCallback(async ({
    status: requestedStatus = "sent",
    recipientsOverride,
    revealDateOverride,
    audienceOverride,
    uploadLocalMedia = true,
    mediaFilesOverride,
  } = {}) => {
    if (isSending) return null;

    const mediaItemsForSend = Array.isArray(mediaFilesOverride) ? mediaFilesOverride : normalizedMediaFiles;
    const hasUploadForSend = mediaItemsForSend.some((m) => shouldUploadWizardMediaItem(m));

    clearError();
    setUploadProgress(hasUploadForSend ? 0 : 100);

    const status = requestedStatus === "draft" ? "draft" : "sent";
    const isDraft = status === "draft";
    const recipientsForSend = Array.isArray(recipientsOverride) ? recipientsOverride : recipients;
    const shouldSaveAsBusinessCapsule = Boolean(isBusinessCapsule);
    const audienceForSend = shouldSaveAsBusinessCapsule ? "self" : (audienceOverride || audience);
    const revealDateForSend =
      revealDateOverride !== undefined ? revealDateOverride : revealDate;
    const finalStatus = isDraft ? "draft" : (shouldSaveAsBusinessCapsule ? "published" : "sent");
    const recipientUserIds = uniq(
      recipientsForSend
        .map((r) => {
          if (!r || typeof r !== "object") return "";
          return normalizeUid(r.uid || r.userId || r.userUid || r.id);
        })
        .filter(Boolean)
    );

    if (!navigator.onLine) {
      setError(true);
      setErrorMessage("No network connection. Please retry when you’re back online.");
      setShowErrorSnack(true);
      return null;
    }

    if (!auth.currentUser?.uid) {
      setError(true);
      setErrorMessage("You must be signed in to send a time capsule.");
      setShowErrorSnack(true);
      return null;
    }

    if (
      shouldSaveAsBusinessCapsule &&
      !isDraft &&
      (
        !String(callToActionText || "").trim() ||
        !String(callToActionUrl || "").trim()
      )
    ) {
      setError(true);
      setErrorMessage("Button text and destination link are required for sponsored capsules.");
      setShowErrorSnack(true);
      return null;
    }

    const existingDraftId = String(draftId || "").trim();

    if (!isDraft && existingDraftId) {
      try {
        const existingRef = doc(db, "timeCapsules", existingDraftId);
        const existingSnap = await getDoc(existingRef);
        if (existingSnap.exists()) {
          const existingData = existingSnap.data() || {};
          const existingStatus = String(existingData?.status || "").toLowerCase();
          const existingLifecycleStatus = String(existingData?.lifecycleStatus || "").toLowerCase();
          const alreadySent =
            existingData?.isDraft === false ||
            ["sent", "published"].includes(existingStatus) ||
            ["sent", "published"].includes(existingLifecycleStatus);

          if (alreadySent) {
            const existingShareToken = String(existingData?.shareToken || "").trim();
            const existingShareUrl =
              String(existingData?.shareUrl || "").trim() ||
              (existingShareToken
                ? `${window.location.origin}/capsule/${existingDraftId}?token=${encodeURIComponent(existingShareToken)}`
                : "");

            setHasSaved(true);
            setSavedDocId(existingDraftId);
            earnedPointsRef.current = 0;
            setEarnedPoints(0);
            setUploadProgress(100);

            return {
              capsuleId: existingDraftId,
              earnedPoints: 0,
              inviteList: [],
              shareUrl: existingShareUrl,
              status: existingStatus || "sent",
              media: Array.isArray(existingData?.media) ? existingData.media : [],
            };
          }
        }
      } catch (existingErr) {
        console.warn("Failed to check existing time capsule before send:", existingErr);
      }
    }

    setIsSending(true);
    let docId = existingDraftId || null;
    let finalShareUrl = "";
    let mediaEntries = [];
    let senderProfileImage = "";
    let senderBusinessId = "";
    let senderBusinessAvatarUrl = "";

    // 1) Upload media (supports multiple attachments)
    if (mediaItemsForSend.length) {
      for (let index = 0; index < mediaItemsForSend.length; index += 1) {
        const item = mediaItemsForSend[index];
        if (!item) continue;

        if (shouldUploadWizardMediaItem(item) && uploadLocalMedia) {
          let uploadFile = null;
          let path = "";
          let storageRef = null;
          let task = null;

          try {
            if (isGooglePlaceImportUrlItem(item)) {
              const imported = await importGooglePlacePhotoToStorage({
                photoUrl: String(item?.url || "").trim(),
                contentType: String(item?.type || "image/jpeg"),
              });
              const importedData = imported?.data || {};
              const importedUrl = String(importedData?.downloadURL || "").trim();
              const importedPath = String(importedData?.storagePath || "").trim();
              if (!importedUrl || !importedPath) {
                throw new Error("Cloud function did not return uploaded place photo details.");
              }
              const mediaEntry = toMediaEntry(
                {
                  ...item,
                  type: item?.type || String(importedData?.contentType || "image/jpeg"),
                  storagePath: importedPath,
                  draftOwnedMedia: true,
                },
                importedUrl
              );
              if (mediaEntry) mediaEntries.push(mediaEntry);
              continue;
            }
            if (isForwardedSourceMediaItem(item)) {
              const copied = await copyForwardedTimeCapsuleMediaToStorage({
                sourceCapsuleId: String(sourceCapsuleId || forwardSourceId || forwardedFromId || "").trim(),
                sourceStoragePath: String(item?.sourceStoragePath || item?.storagePath || "").trim(),
                sourceUrl: String(item?.url || "").trim(),
                contentType: String(item?.type || "").trim(),
                sourceShareToken: String(sourceShareToken || "").trim(),
              });
              const copiedData = copied?.data || {};
              const copiedUrl = String(copiedData?.downloadURL || "").trim();
              const copiedPath = String(copiedData?.storagePath || "").trim();
              const copiedType = String(
                item?.type || copiedData?.contentType || "application/octet-stream"
              ).trim();
              if (!copiedUrl || !copiedPath) {
                throw new Error("Cloud function did not return copied media details.");
              }

              const copiedSessionPreviewUrl = String(item?.sessionPreviewUrl || "").trim();
              const isCopiedVideo = copiedType.toLowerCase().startsWith("video/");
              let copiedPosterMetadata = {};
              if (copiedSessionPreviewUrl && isCopiedVideo) {
                try {
                  copiedPosterMetadata = await uploadVideoPosterThumbnail({
                    sessionPreviewUrl: copiedSessionPreviewUrl,
                    sourceName: item?.name || `forwarded-video-${index}.mp4`,
                    index,
                  });
                } catch (posterErr) {
                  console.warn("Unable to upload forwarded video thumbnail before save:", posterErr);
                }
              }

              const mediaEntry = toMediaEntry(
                {
                  ...item,
                  url: copiedUrl,
                  type: copiedType,
                  storagePath: copiedPath,
                  sourceStoragePath: "",
                  draftOwnedMedia: true,
                  forwardedSourceMedia: false,
                  posterStoragePath: "",
                  ...copiedPosterMetadata,
                  ...(copiedSessionPreviewUrl ? { sessionPreviewUrl: copiedSessionPreviewUrl } : {}),
                },
                copiedUrl
              );
              if (mediaEntry) mediaEntries.push(mediaEntry);
              setUploadProgress(Math.round(((index + 1) / mediaItemsForSend.length) * 100));
              continue;
            }

            uploadFile = await buildUploadableMediaFile(item, index);
            if (!uploadFile) {
              continue;
            }

            path = `timecapsules/${Date.now()}-${index}-${uploadFile.name}`;
            storageRef = ref(storage, path);
            const metadata = uploadFile?.type ? { contentType: uploadFile.type } : undefined;
            await uploadWithRetries({
              storageRef,
              file: uploadFile,
              metadata,
              maxRetries: 2,
              stallMs: 25000,
              onTask: (nextTask) => {
                task = nextTask;
                uploadTaskRef.current = nextTask;
              },
              onProgress: (snapshot) => {
                const rawPct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                const overallPct = Math.round(((index + rawPct / 100) / mediaItemsForSend.length) * 100);
                setUploadProgress(overallPct);
              },
            });

            const downloadURL = await getDownloadURL(storageRef);
            let ensuredSessionPreviewUrl = String(item?.sessionPreviewUrl || "").trim();
            const isVideoUpload = String(item?.type || uploadFile?.type || "")
              .toLowerCase()
              .startsWith("video/");
            if (!ensuredSessionPreviewUrl && isVideoUpload && uploadFile instanceof File) {
              ensuredSessionPreviewUrl = await buildVideoPreviewBestEffort(uploadFile);
            }
            let videoPosterMetadata = {};
            if (ensuredSessionPreviewUrl && isVideoUpload) {
              try {
                videoPosterMetadata = await uploadVideoPosterThumbnail({
                  sessionPreviewUrl: ensuredSessionPreviewUrl,
                  sourceName: uploadFile?.name || item?.name || `video-${index}.mp4`,
                  index,
                });
              } catch (posterErr) {
                console.warn("Unable to upload video thumbnail before save:", posterErr);
              }
            }
            const mediaEntry = toMediaEntry(
              {
                ...item,
                type: item?.type || uploadFile?.type || "image/jpeg",
                storagePath: path,
                draftOwnedMedia: true,
                ...videoPosterMetadata,
                ...(ensuredSessionPreviewUrl ? { sessionPreviewUrl: ensuredSessionPreviewUrl } : {}),
              },
              downloadURL
            );
            if (mediaEntry) mediaEntries.push(mediaEntry);
          } catch (uploadErr) {
            console.error("Upload error:", uploadErr);
            setError(true);

            const recoverableFailure =
              uploadErr?.code === "storage/retry-limit-exceeded" ||
              uploadErr?.code === "storage/canceled" ||
              uploadErr?.code === "storage/network-request-failed";

            setErrorMessage(
              recoverableFailure
                ? "Upload interrupted by network conditions. Tap Retry."
                : "Upload failed. Tap Retry."
            );

            setShowErrorSnack(true);
            setIsSending(false);
            return null;
          } finally {
            if (uploadTaskRef.current === task) {
              uploadTaskRef.current = null;
            }
          }
        } else if (!shouldUploadWizardMediaItem(item)) {
          const mediaEntry = toMediaEntry(item, null);
          if (mediaEntry) mediaEntries.push(mediaEntry);
        }
      }
    }

    const geohash = ngeohash.encode(lat, lng);

    // 3) Save to Firestore (status: "sent" triggers backend award)
    try {
      try {
        const senderSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (senderSnap.exists()) {
          const senderData = senderSnap.data() || {};
          senderProfileImage = senderData.profileImage || "";
          senderBusinessId = String(
            senderData.businessId ||
            senderData.studioBusinessId ||
            senderData.business?.id ||
            ""
          ).trim();
          senderBusinessAvatarUrl = String(
            senderData.businessAvatarUrl ||
            senderData.businessLogoUrl ||
            senderData.business?.avatarUrl ||
            senderData.business?.logoUrl ||
            ""
          ).trim();
        }
      } catch (profileErr) {
        console.warn("Unable to fetch sender profile image before save:", profileErr);
      }

      const revealTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const senderUid = auth.currentUser.uid;
      const finalRecipientUserIds = shouldSaveAsBusinessCapsule
        ? []
        : uniq([
            ...(audienceForSend === "self" ? [senderUid] : []),
            ...recipientUserIds,
          ]);
      const revealDateValue = parseOptionalDate(revealDateForSend);
      const revealDateUtcIso = revealDateValue ? revealDateValue.toISOString() : "";
      const endDateValue = parseOptionalDate(endDate);
      const normalizedHashtags = Array.from(
        new Set(
          (Array.isArray(hashtags) ? hashtags : [])
            .map((tag) => String(tag || "").trim().replace(/^#+/, "").toLowerCase())
            .filter(Boolean)
        )
      ).slice(0, 3);
      const resolvedForwardSourceId = String(
        sourceCapsuleId || forwardSourceId || forwardedFromId || ""
      ).trim();
      const basePayload = {
        senderId: senderUid,
        senderProfileImage,
        audience: audienceForSend,
        isSelfCapsule: !shouldSaveAsBusinessCapsule && audienceForSend === "self",
        location: { address, placeId, lat, lng },
        geohash,
        media: mediaEntries,
        messageTitle: subject,
        messageBody: message,
        seedType: normalizeSeedType(seedType),
        revealDate: revealDateValue,
        revealTimezone: revealDateValue ? revealTimezone : "",
        revealDateUtcIso,

        recipientPhoneNumbers: [],
        recipientUserIds: finalRecipientUserIds,

        newRecipientPhoneNumbers: [],
        deliveryMode: shouldSaveAsBusinessCapsule ? "public_sponsored" : "share_claim",
        shareToken: "",
        shareUrl: "",
        claimedBy: isDraft || shouldSaveAsBusinessCapsule ? [] : finalRecipientUserIds,
        claimedAtByUid: {},
        draftStep: isDraft ? draftStep : "",
        isDraft,
        lifecycleStatus: finalStatus,
        lastEditedAt: serverTimestamp(),

        capsuleType: shouldSaveAsBusinessCapsule ? "sponsored" : "private",
        sentAt: isDraft ? null : serverTimestamp(),
        status: finalStatus,
        // Persist the forward reference so recipients (and future
        // attribution/rewards) can resolve the original capsule.
        ...(resolvedForwardSourceId
          ? {
              sourceCapsuleId: resolvedForwardSourceId,
              ...(String(forwardedFromType || "").trim()
                ? { forwardedFromType: String(forwardedFromType).trim() }
                : {}),
            }
          : {}),
        ...(shouldSaveAsBusinessCapsule
          ? {
              businessId: senderBusinessId,
              ...(senderBusinessAvatarUrl ? { businessAvatarUrl: senderBusinessAvatarUrl } : {}),
              hashtags: normalizedHashtags,
              endDate: endDateValue || null,
              callToActionText: String(callToActionText || "").trim(),
              callToActionUrl: String(callToActionUrl || "").trim(),
            }
          : {}),
      };

      let docRef;
      if (existingDraftId) {
        docRef = doc(db, "timeCapsules", existingDraftId);
        await updateDoc(docRef, basePayload);
      } else {
        docRef = await addDoc(collection(db, "timeCapsules"), {
          ...basePayload,
          createdAt: serverTimestamp(),
        });
        docId = docRef.id;
      }

      docId = docId || docRef.id;

      if (!isDraft && !shouldSaveAsBusinessCapsule) {
        const existingShareToken = String((await getDoc(docRef)).data()?.shareToken || "").trim();
        const shareToken = existingShareToken || crypto.randomUUID();
        finalShareUrl = `${window.location.origin}/capsule/${docId}?token=${encodeURIComponent(shareToken)}`;
        await updateDoc(docRef, {
          shareToken,
          shareUrl: finalShareUrl,
          sentAt: serverTimestamp(),
          status: "sent",
          lifecycleStatus: "sent",
          isDraft: false,
          draftStep: "",
          claimedBy: finalRecipientUserIds,
        });
      }
      setHasSaved(true);
      setSavedDocId(docId);

      let preview = 0;
      if (!isDraft && !shouldSaveAsBusinessCapsule) {
        try {
          preview = await computeEarnedPointsPreview({
            recipientCount: finalRecipientUserIds.length,
            newRecipientCount: 0,
          });
        } catch (e) {
          console.warn("computeEarnedPointsPreview failed:", e);
          preview = 0;
        }
      }

      earnedPointsRef.current = preview;
      setEarnedPoints(preview);

      const capsulePreview = !isDraft && !shouldSaveAsBusinessCapsule
        ? buildCapsulePreviewPayload({
            docId,
            senderUid,
            senderProfileImage,
            audience: audienceForSend,
            address,
            placeId,
            lat,
            lng,
            mediaEntries,
            subject,
            message,
            seedType,
            revealDate: revealDateValue,
            finalRecipientUserIds,
            finalStatus: "sent",
          })
        : null;

      return {
        capsuleId: docId,
        earnedPoints: earnedPointsRef.current,
        inviteList: [],
        shareUrl: finalShareUrl,
        status: finalStatus,
        media: mediaEntries,
        capsulePreview,
      };
    } catch (dbErr) {
      console.error("Error saving time capsule:", dbErr);
      setError(true);
      setErrorMessage("Failed to save time capsule. Tap Retry.");
      setShowErrorSnack(true);
      setIsSending(false);
      return null;
    } finally {
      setIsSending(false);
    }
  }, [
    address,
    clearError,
    computeEarnedPointsPreview,
    copyForwardedTimeCapsuleMediaToStorage,
    importGooglePlacePhotoToStorage,
    isSending,
    lat,
    lng,
    audience,
    placeId,
    normalizedMediaFiles,
    message,
    seedType,
    revealDate,
    endDate,
    callToActionText,
    callToActionUrl,
    hashtags,
    isBusinessCapsule,
    draftId,
    draftStep,
    sourceCapsuleId,
    forwardSourceId,
    forwardedFromId,
    forwardedFromType,
    sourceShareToken,
    subject,
    recipients,
  ]);

  return {
    isSending,
    hasUpload,
    hasSaved,
    savedDocId,
    uploadProgress,
    error,
    errorMessage,
    showErrorSnack,
    earnedPoints,
    earnedPointsRef,
    setShowErrorSnack,
    clearError,
    send,
  };
}
